import LoadingSkeleton from "@shared/LoadingSkeleton";
import { formatStringCase, getTitleCase } from "@utils/utils";
import ReactTooltip from "react-tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { handleCopy } from "@helpers/profileHelper";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generateTrackingObject, getInitials } from "@core/global/global-country-functions";
import { useDispatch, useSelector } from "react-redux";
import { apolloContactsDataHandler } from "@redux/ProfileContacts/profileContactsSlice";
import axios from "axios";
import { toast } from "react-toastify";
import { captureException } from "@sentry/browser";
import { profileTypeEndPoint, profileTypeEnum } from "@components/templates/Profile/ProfileHeader/profile-enum";
import NewContactForm from "@routes/intel/us/company-profile/newContactForm";
import LocalStorageService from "@core/services/local-storage.service";
import { SelectionBar } from "../components/SelectionBar";
import RequestContactFormNew from "../../../shared/request-contacts-form-new";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { getSidebarCountData } from "@redux/Sidebar/sidebarCount.action";
import UpgradeIconSmall from "@assets/images/zap-icon.svg";
import GetInTouchPopup from "@components/templates/Profile/ShipmentDistribution/GetInTouchPopup";
import { Modal, Popover } from "react-bootstrap";
import Loader from "@/components/base/loader/Loader";
import ReactTableTrademo from "@shared/trademo-datatable/react-table-trademo";
import { ERROR_MSG } from "@utils/enum";

const localStorageService = LocalStorageService.getService();

const dropdownFields = {
  department: "department",
  seniority: "seniority",
  location: "location",
};

const tabEnum = {
  searchContact: "searchContact",
  viewedContact: "viewedContact",
  scanContact: "scanContact",
};

const credits = {
  email: { credit: 1, key: "isEmailViewed" },
  phone: { credit: 5, key: "isPhoneViewed" },
  both: { credit: 6, key: ["isEmailViewed", "isPhoneViewed"] },
  linkedin: { credit: 0, key: "isLinkedInViewed" },
};

const initialPageSize = 10;

// ReactTableTrademo paginates internally (manualPagination is off), so its Next button is enabled
// only while the data array still has rows ahead. Padding the fetched prefix with empty placeholder
// rows up to the next page boundary keeps Next clickable so onChangePage can fetch the real rows.
const padContactList = (list, total, size) => {
  const padded = [...list];
  const target = Math.min(total, (Math.floor(padded.length / size) + 1) * size);
  while (padded.length < target) padded.push({});
  return padded;
};

const isPlaceholderRow = (row) => !row || Object.keys(row).length === 0;

const isFailedContactsResponse = (res) => res?.data?.status === "failed" || !res?.data?.data;

const reportContactsFetchFailure = (apiName, res, requestObject) => {
  captureException(new Error(`${apiName} responded ${res?.status ?? "unknown"} with a failure payload: ${res?.data?.message || "no data in response"}`), {
    tags: {
      feature: "company-profile-contacts",
      api: apiName,
      failure_type: "http_200_failure_payload",
    },
    extra: {
      httpStatus: res?.status,
      responseBody: res?.data,
      companyId: requestObject?.company_id,
      companyName: requestObject?.company_name,
      pageNumber: requestObject?.page_number,
      pageSize: requestObject?.size,
      contactIds: requestObject?.contact_ids,
    },
  });
};

const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
    <path d="M168-192q-29 0-50.5-21.5T96-264v-432q0-29 21.5-50.5T168-768h624q30 0 51 21.5t21 50.5v432q0 29-21 50.5T792-192H168Zm312-240 312-179v-85L480-517 168-696v85l312 179Z" />
  </svg>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
    <path d="M763-145q-121-9-229.5-59.5T339-341q-86-86-136-194.5T144-765q-2-21 12.5-36.5T192-817h136q17 0 29.5 10.5T374-780l24 107q2 13-1.5 25T385-628l-97 98q20 38 46 73t58 66q30 30 64 55.5t72 45.5l99-96q8-8 20-11.5t25-1.5l107 23q17 5 27 17.5t10 29.5v136q0 21-16 35.5T763-145Z" />
  </svg>
);

// Widens the popover to the trigger's width plus 10px, instead of hugging its own content.
const sameWidthModifier = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width + 10}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.offsetWidth + 10}px`;
  },
};

// Shifts the popover up by the reference's own height plus 15px, so its top edge lands 15px
// above the reference's top edge — the overlay overlaps the row rather than floating under it.
const overlapOffsetModifier = {
  name: "offset",
  options: {
    offset: ({ reference }) => [0, -reference.height - 10],
  },
};

const popoverPopperConfig = { modifiers: [sameWidthModifier, overlapOffsetModifier] };

const renderCopyableDetails = (entries) =>
  entries.length > 0 && (
    <OverlayTrigger
      trigger="click"
      rootClose
      placement="bottom-start"
      popperConfig={popoverPopperConfig}
      overlay={
        <Popover className="tw-border-0 tw-shadow-none tw-bg-transparent tw-max-w-none [&>.arrow]:tw-hidden">
          <Popover.Content className="tw-flex tw-flex-col tw-gap-2 tw-p-2 tw-bg-primary-purple-50">
            {entries.map(({ icon, value }) => (
              <div key={value} className="tw-flex tw-items-center tw-self-start tw-gap-2 tw-whitespace-nowrap tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-shadow-autoSuggestPill tw-px-3 tw-py-2 tw-text-sm">
                <span className="tw-flex tw-items-center tw-gap-1">
                  {icon}
                  {value}
                </span>
                <svg onClick={() => handleCopy(value)} className="tw-cursor-pointer" xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                  <path d="M360-240q-29.7 0-50.85-21.15Q288-282.3 288-312v-480q0-29.7 21.15-50.85Q330.3-864 360-864h384q29.7 0 50.85 21.15Q816-821.7 816-792v480q0 29.7-21.15 50.85Q773.7-240 744-240H360Zm0-72h384v-480H360v480ZM216-96q-29.7 0-50.85-21.15Q144-138.3 144-168v-552h72v552h456v72H216Zm144-216v-480 480Z" />
                </svg>
              </div>
            ))}
          </Popover.Content>
        </Popover>
      }>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-cursor-pointer tw-w-full tw-min-w-0">
        {entries.map(({ value }) => (
          <div key={value} className="tw-flex tw-max-w-full tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-shadow-autoSuggestPill tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-90">
            <span className="tw-truncate tw-min-w-0">{value}</span>
          </div>
        ))}
      </div>
    </OverlayTrigger>
  );

const SearchContacts = (props) => {
  const { data, dataSource, pathName, currentUserPlan, eventName, setCurrentScanTab, viewedContactList = [], currentTab, setValueAddCredit, headerExtra, onLoadingChange } = props;
  const [internalLoader, setInternalLoader] = useState(false);

  useEffect(() => {
    onLoadingChange?.(internalLoader);
  }, [internalLoader, onLoadingChange]);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [successModal, setSuccessModal] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [apolloCompanyId, setApolloCompanyId] = useState([]);
  const [uniqueId, setUniqueId] = useState("");
  const [apolloSource, setApolloSource] = useState("");
  const [clearFilter, setClearFilter] = useState(false);
  const [contactsDataList, setContactsDataList] = useState([]);
  const [totalContactCount, setTotalContactCount] = useState(0);
  const [contactSearched, setContactSearched] = useState(false);
  const [isDataPresent, setisDataPresent] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [viewContactList, setViewContactList] = useState([]);
  const [selectedValues, setSelectedValues] = useState({ department: [], seniority: [], location: [], job_title: [] });
  const creditDetails = useSelector((state) => state.userDetails.creditDetails);
  const STARTERPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("starter"))?.navigate === "search";
  const STARTERPLANCOUNTRY = creditDetails?.data[0]?.user_active_plan;
  const [showGetInTouch, setShowGetInTouch] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (!clearSelectedRows) return undefined;
    const timer = setTimeout(() => setClearSelectedRows(false), 0);
    return () => clearTimeout(timer);
  }, [clearSelectedRows]);

  // Kept in refs (rather than closed over directly) so the cell-renderer callbacks below can stay
  // referentially stable across renders — ReactTableTrademo resets row selection whenever its
  // `columns` prop identity changes, and columns are built from these renderers.
  const selectedContactsRef = useRef(selectedContacts);
  selectedContactsRef.current = selectedContacts;
  const dataRef = useRef(data);
  dataRef.current = data;

  const handleViewAction = useCallback((item, type) => {
    if (dataRef.current.creditsLoading) return;
    let el = item;
    let reqCredits = 0;
    if (!el) {
      el = JSON.parse(JSON.stringify(Object.values(selectedContactsRef.current)));
      reqCredits = calReqCredits(el, type);
    } else {
      reqCredits = credits[type].credit;
    }
    if ((type === "both" || type === "email" || type === "phone") && dataRef.current.valueAddCredit < reqCredits) {
      return toast.error("You do not have enough value add credit(s) to fetch the contacts. Please reach out to customersuccess@trademo.com for more credits.");
    } else {
      setPendingAction({ item, type });
      setConfirmationModal(true);
    }
  }, []);

  const handleConfirm = () => {
    if (pendingAction) {
      callingDetailedContact(!pendingAction.item ? pendingAction.item : [pendingAction.item], pendingAction.type);
      setConfirmationModal(false);
      setPendingAction(null);
    }
  };

  const handleModalClose = () => {
    setConfirmationModal(false);
    setPendingAction(null);
  };

  const { logisticsProfile, companyProfile, profileContactData } = useSelector((state) => state);

  // Called by the table with the 1-based page the user is moving to; fetch it if we only have
  // placeholders there. During a size change the table reports the new size before
  // onChangeRowsPerPage fires — skip that call, handleTablePageSizeChange refetches anyway.
  const handleTablePageChange = (pageNum, size) => {
    if (currentTab !== tabEnum.searchContact || size !== pageSize || pageNum < 1) return;
    if (isPlaceholderRow(contactsDataList[(pageNum - 1) * pageSize])) {
      // currentPage still holds the page we're leaving — the table reports the new page (via
      // onTableObjectChange) only after this callback. On fetch failure, roll back to it so the
      // table remounts on the page that has real data instead of the placeholder page.
      const prevPage = currentPage;
      const requestObject = createPayload();
      requestObject.page_number = pageNum;
      callContactsResults(selectedValues, requestObject, () => setCurrentPage(prevPage));
    }
  };
  const handleTablePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    // Cached pages are keyed to the old size, so refetch page 1 with the new size
    if (currentTab === tabEnum.searchContact) {
      const requestObject = createPayload();
      requestObject.size = newSize;
      callContactsResults(selectedValues, requestObject);
    }
  };
  // Keeps parent page state in sync with the table's internal page so the table remounts on the
  // same page after a fetch (the loading skeleton unmounts it) and after react-table's auto-resets.
  const handleTableObjectChange = (tableObject) => {
    setCurrentPage((tableObject?.currentPage ?? 0) + 1);
  };
  const dispatch = useDispatch();
  useEffect(() => {
    if (currentTab === tabEnum.searchContact) {
      if (profileContactData?.apolloContactsData && Object.values(profileContactData?.apolloContactsData).length > 0) {
        if (profileContactData?.apolloContactsData.source) setApolloSource(profileContactData.apolloContactsData.source || "");
        if (profileContactData?.apolloContactsData.apollo_company_id) setApolloCompanyId([...profileContactData.apolloContactsData.apollo_company_id]);
        if (profileContactData?.apolloContactsData.unique_id) setUniqueId(profileContactData?.apolloContactsData.unique_id);
        // Cached prefixes are always a multiple of 10 (all page sizes are), so padding at the
        // initial size keeps page boundaries aligned with the restored rows.
        if (profileContactData?.apolloContactsData.contactList) setContactsDataList(padContactList(profileContactData.apolloContactsData.contactList, profileContactData.apolloContactsData.totalContactCount || profileContactData.apolloContactsData.contactList.length, initialPageSize));
        if (profileContactData?.apolloContactsData.totalContactCount) setTotalContactCount(profileContactData.apolloContactsData.totalContactCount);
        if (profileContactData?.apolloContactsData?.totalContactCount > 0) setisDataPresent(true);
        setContactSearched(true);
      } else {
        callContactsResults(selectedValues);
      }
      setViewContactList([]);
    }
    if (currentTab === tabEnum.viewedContact) {
      setViewContactList([...viewedContactList]);
      setTotalContactCount(viewedContactList.length);
      setContactsDataList([...viewedContactList]);
    }

    setSelectedContacts({});
    setCurrentPage(1);
    setPageSize(initialPageSize);
  }, [currentTab]);

  useEffect(() => {
    if (currentTab !== tabEnum.viewedContact) return;
    setViewContactList([...viewedContactList]);
    setTotalContactCount(viewedContactList.length);
    setContactsDataList([...viewedContactList]);
  }, [viewedContactList]);

  const createPayload = () => {
    const requestObject = {
      company_name: data?.companyData?.name || "",
      company_id: data?.companyData?.id || "",
      companyContactDict: { country: data.companyAddress },
      last_id: "",
      from: 0,
      search_key: "",
      page_number: 1,
      size: pageSize,
      job_title: [],
    };
    return requestObject;
  };
  const callingDetailedContact = (item, type) => {
    trademoTrackingEvents(trackingEventDictionary.companyProfilePageSearchContactViewContactDetails, generateTrackingObject(dataSource, pathName, currentUserPlan));
    let list = item;
    if (!list) {
      list = JSON.parse(JSON.stringify(Object.values(selectedContacts)));
    }

    if (type === "phone") {
      list = list.filter((ele) => !ele?.isPhoneViewed && (ele?.isPhoneAvailable || ele?.hasPhone));
    }
    if (type === "email") {
      list = list.filter((ele) => !ele?.isEmailViewed && (ele?.isEmailAvailable || ele?.hasEmail));
    }
    if (type === "both") {
      list = list.filter((ele) => !((ele?.isEmailViewed || ele?.isLinkedInViewed) && ele?.isPhoneViewed) && ele?.hasPhone && (ele?.hasEmail || ele?.hasLinkedIn));
      list = list.map((ele) => (ele.isViewed ? (ele?.isEmailViewed && ele?.hasPhone ? { ...ele, type: "phone" } : ele?.isPhoneViewed && ele?.hasEmail ? { ...ele, type: "email" } : { ...ele, type: "linkedin" }) : { ...ele, type: "" }));
    }
    const params = {
      company_name: data.companyData.name,
      company_id: data.companyData.id,
      plan_id: !!localStorageService.getUserCurrentPlan() && localStorageService.getUserCurrentPlan() !== "" ? localStorageService.getUserCurrentPlan() : "",
    };
    let contact_ids = [];
    for (let i = 0; i < list.length; i++) {
      let obj = {};
      obj.id = list[i].contactId;
      obj.location = list[i].location;
      obj.source = list[i].source;
      obj.request_id = list[i].requestId;
      obj.contact_type = list?.[i]?.type ? list[i].type : type;
      obj.hasEmail = list?.[i]?.hasEmail;
      obj.hasPhone = list?.[i]?.hasPhone;
      obj.hasLinkedIn = list?.[i]?.hasLinkedIn;
      if (list?.[i]?.object_id) obj.object_id = list[i].object_id;
      contact_ids.push(obj);
    }
    params.contact_ids = JSON.parse(JSON.stringify(contact_ids));
    params.unique_id = JSON.parse(JSON.stringify(uniqueId));
    if (logisticsProfile?.portCategory || companyProfile?.companyCategory) {
      params.redirectionType = logisticsProfile?.portCategory || companyProfile?.companyCategory || "";
    }
    const contactDetailsApiUrl = profileTypeEndPoint.contactDetailsApiUrl;
    setInternalLoader(true);
    axios
      .post(contactDetailsApiUrl, params)
      .then((res) => {
        if (isFailedContactsResponse(res)) {
          reportContactsFetchFailure("getContactDetails", res, params);
          toast.error(ERROR_MSG);
          return;
        }
        if (res?.data?.data?.contact_details_list && Array.isArray(res.data.data.contact_details_list)) {
          if (currentTab === tabEnum.viewedContact) mapContacts(res.data.data.contact_details_list);
          else mapViewedContacts(res.data.data.contact_details_list);
          if (!list.some((ele) => ele.isViewed) && currentTab === tabEnum.searchContact) {
            dispatch(getSidebarCountData());
          }
          toast.success("Contact has been saved in the Viewed Contacts for this company and Address Book in the Dashboard.");
        }
        if (res?.data?.data) {
          setValueAddCredit?.(res?.data?.data.remaining_value_add_credit);
        }
      })
      .catch((error) => {
        captureException(error);
        toast.error(error?.response?.data?.message || ERROR_MSG);
      })
      .finally(() => {
        setInternalLoader(false);
      });
  };
  const callContactsResults = (val, payload, onError) => {
    const requestObject = payload ? payload : createPayload();
    if (!requestObject.company_id || !requestObject.company_name) return;
    setIsLoading(true);
    let isFilterApplieFlag = false;
    for (let [key, value] of Object.entries(selectedValues)) {
      let tempValue = JSON.parse(JSON.stringify(val?.[key] || (Array.isArray(value) && value) || []));
      if (tempValue && Array.isArray(tempValue) && tempValue.length > 0 && !isFilterApplieFlag) {
        isFilterApplieFlag = true;
      }
      requestObject[key] = tempValue.map((ele) => ele.value);
    }
    setIsFilterApplied(isFilterApplieFlag);
    if (val) {
      setSelectedValues(JSON.parse(JSON.stringify(val)));
    }
    if (requestObject.page_number !== 1) {
      requestObject.apollo_company_id = [...apolloCompanyId];
      requestObject.source = apolloSource;
    } else {
      setSelectedContacts({});
      setCurrentPage(1);
      setContactsDataList([]);
      setContactSearched(false);
    }
    requestObject.job_title = val?.job_title ?? [];
    const contactApiUrl = profileTypeEndPoint.viewContacts;
    return axios
      .post(contactApiUrl, requestObject)
      .then((res) => {
        if (isFailedContactsResponse(res)) {
          reportContactsFetchFailure("getCompanyContactsList", res, requestObject);
          if (requestObject.page_number === 1) {
            setContactsDataList([]);
            setTotalContactCount(0);
            setContactSearched(true);
          } else {
            toast.error(ERROR_MSG);
          }
          onError?.();
          return;
        }
        if (res?.data?.data?.contactList && Array.isArray(res.data.data.contactList) && requestObject.page_number !== 1) {
          let tempContactList = JSON.parse(JSON.stringify(contactsDataList));
          let pointer = 0;
          const requestSize = requestObject.size || pageSize;
          for (let i = (requestObject.page_number - 1) * requestSize; i < Math.min(requestObject.page_number * requestSize, totalContactCount); i++) {
            tempContactList[i] = { ...res.data.data.contactList?.[pointer] };
            pointer++;
          }
          setContactsDataList(padContactList(tempContactList, totalContactCount, requestSize));
        } else if (res?.data?.data) {
          if (res.data.data.source) setApolloSource(res.data.data.source || "");
          if (res.data.data.apollo_company_id) setApolloCompanyId([...res.data.data.apollo_company_id]);
          if (res.data.data.unique_id) setUniqueId(res.data.data.unique_id);
          if (res.data.data.contactList) setContactsDataList(padContactList(res.data.data.contactList, res.data.data.totalContactCount || res.data.data.contactList.length, requestObject.size || pageSize));
          if (res.data.data.totalContactCount) setTotalContactCount(res.data.data.totalContactCount);
          setContactSearched(true);
          if (!isFilterApplieFlag) {
            if (res.data.data.totalContactCount > 0) setisDataPresent(true);
            dispatch(apolloContactsDataHandler(res.data.data));
          }
        }
      })
      .catch((error) => {
        captureException(error);
        toast.error(error?.response?.data?.message || ERROR_MSG);
        onError?.();
      })
      .finally(() => {
        setIsLoading(false);
        if (clearFilter) {
          setClearFilter(false);
        }
      });
  };
  const mapViewedContacts = (contactArr) => {
    let tempSelectedContacts = JSON.parse(JSON.stringify(selectedContacts));
    let tempContactList = JSON.parse(JSON.stringify(contactsDataList));
    for (let i = 0; i < contactArr.length; i++) {
      for (let j = 0; j < tempContactList.length; j++) {
        if (tempContactList[j]?.contactId === contactArr[i]?.hashedContactId) {
          tempContactList[j] = { ...tempContactList[j], email: contactArr[i].email, phone: contactArr[i].phone, fullName: contactArr[i].fullName, isViewed: true, isPhoneViewed: contactArr[i].isPhoneViewed, isEmailViewed: contactArr[i].isEmailViewed, isLinkedInViewed: contactArr[i].isLinkedInViewed, object_id: contactArr[i].object_id, linkedin_url: contactArr[i].linkedin_url };
          if (tempSelectedContacts[contactArr[i].hashedContactId]) {
            delete tempSelectedContacts[contactArr[i].hashedContactId];
          }
        }
      }
    }
    setSelectedContacts(JSON.parse(JSON.stringify(tempSelectedContacts)));
    setContactsDataList(JSON.parse(JSON.stringify(tempContactList)));
    dispatch(apolloContactsDataHandler({ ...profileContactData?.apolloContactsData, contactList: tempContactList.slice(0, pageSize) }));
  };
  const mapContacts = (contactArr) => {
    let tempContactList = JSON.parse(JSON.stringify(viewContactList));
    for (let i = 0; i < contactArr.length; i++) {
      for (let j = 0; j < tempContactList.length; j++) {
        if (tempContactList[j]?.contactId === contactArr[i]?.hashedContactId) {
          tempContactList[j] = { ...tempContactList[j], email: contactArr[i].email, phone: contactArr[i].phone, fullName: contactArr[i].fullName, isViewed: true, isPhoneViewed: contactArr[i].isPhoneViewed, isEmailViewed: contactArr[i].isEmailViewed, object_id: contactArr[i].object_id, isLinkedInViewed: contactArr[i].isLinkedInViewed, linkedin_url: contactArr[i].linkedin_url };
        }
      }
    }
    setViewContactList(JSON.parse(JSON.stringify(tempContactList)));
  };

  const renderContactDetailsCell = useCallback(
    (item) => (
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-w-full">
        {item.isViewed && renderCopyableDetails([item.email && { icon: <EmailIcon />, value: item.email }, item.phone && { icon: <PhoneIcon />, value: item.phone }].filter(Boolean))}
        {item?.hasPhone && !item?.isPhoneViewed && (
          <span className="cursor-pointer" onClick={() => handleViewAction(item, "phone")}>
            <button type="button" className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-md tw-shadow-autoSuggestPill tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-900 hover:tw-bg-gray-50">
              <PhoneIcon /> Phone
            </button>
          </span>
        )}
        {item?.hasEmail && !item?.isEmailViewed && (
          <span className="cursor-pointer" onClick={() => handleViewAction(item, "email")}>
            <button type="button" className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-md tw-shadow-autoSuggestPill tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-900 hover:tw-bg-gray-50">
              <EmailIcon /> Email
            </button>
          </span>
        )}
        {((item?.hasEmail && !item?.isEmailViewed) || (item?.hasLinkedIn && !item?.isLinkedInViewed)) && item?.hasPhone && !item?.isPhoneViewed && (
          <span className="cursor-pointer" onClick={() => handleViewAction(item, "both")}>
            <button type="button" className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-md tw-shadow-autoSuggestPill tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-900 hover:tw-bg-gray-50">
              <EmailIcon /> <PhoneIcon /> Both
            </button>
          </span>
        )}
      </div>
    ),
    [handleViewAction]
  );

  const renderNameCell = useCallback(
    (item) => (
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
          {item?.profilePicUrl ? <img src={item.profilePicUrl} className="tw-rounded-full tw-w-7 tw-h-7 tw-min-w-[28px] tw-object-cover" alt={getInitials(item.fullName)} /> : <div className="tw-rounded-full tw-w-7 tw-h-7 tw-min-w-[28px] tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-medium tw-bg-[#ffd964]">{(item.fullName && item.fullName.trim().charAt(0)) || "NA"}</div>}
          <span className="tw-text-sm tw-font-medium tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
            {(item.fullName && formatStringCase(item.fullName.slice(0, 45), false, true)) || "NA"}
            {(item?.fullName?.length ?? 0) > 45 && "..."}
          </span>
        </div>
        {item.linkedin_url && (
          <a
            className="tw-flex-shrink-0 tw-flex tw-items-center tw-cursor-pointer"
            href={item.linkedin_url || undefined}
            target={item.linkedin_url ? "_blank" : undefined}
            rel="noreferrer"
            onClick={(e) => {
              if (!item.linkedin_url) e.preventDefault();
            }}>
            <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1784705761/intel/icons/linkedin_1_az6r2d.svg" alt="linkedin" className="tw-w-5 tw-h-5" />
          </a>
        )}
      </div>
    ),
    []
  );

  const renderJobTitleCell = useCallback(
    (item) =>
      item.titles ? (
        <>
          <span data-for={`job_profile${item.contact_id}`} data-tip={item.titles.join(", ").length > 30 ? getTitleCase(item.titles.join(", ")) : ""}>
            {formatStringCase(item.titles.join(", "), false, true)}
          </span>
          <ReactTooltip className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="bottom" id={`job_profile${item.contact_id}`} />
        </>
      ) : (
        "-"
      ),
    []
  );

  const renderViewedContactDetailsCell = useCallback(
    (item) => (
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-w-full">
        {renderCopyableDetails([item.email && { icon: <EmailIcon />, value: item.email }, item.phone && { icon: <PhoneIcon />, value: item.phone }].filter(Boolean))}
        {!item?.isPhoneViewed && item.isPhoneAvailable && (
          <span className="cursor-pointer" onClick={() => handleViewAction(item, "phone")}>
            <button type="button" className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-md tw-shadow-autoSuggestPill tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-900 hover:tw-bg-gray-50">
              <PhoneIcon /> Phone
            </button>
          </span>
        )}
        {!item?.isEmailViewed && item.isEmailAvailable && (
          <span className="cursor-pointer" onClick={() => handleViewAction(item, "email")}>
            <button type="button" className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-md tw-shadow-autoSuggestPill tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-900 hover:tw-bg-gray-50">
              <EmailIcon /> Email
            </button>
          </span>
        )}
      </div>
    ),
    [handleViewAction]
  );

  const searchColumns = useMemo(
    () => [
      { name: "Name", selector: (item) => renderNameCell(item) },
      { name: "Job Title", selector: (item) => renderJobTitleCell(item) },
      { name: "Address", selector: (item) => (item.location && item.location.length > 0 && item.location) || "-" },
      { name: "Contact Details", selector: (item) => renderContactDetailsCell(item), miWidth: "260px", maWidth: "260px" },
    ],
    [renderNameCell, renderJobTitleCell, renderContactDetailsCell]
  );

  const viewedColumns = useMemo(
    () => [
      { name: "Name", selector: (item) => renderNameCell(item) },
      { name: "Job Title", selector: (item) => renderJobTitleCell(item) },
      { name: "Address", selector: (item) => (item.location && item.location.length > 0 && item.location) || "-" },
      { name: "Contact Details", selector: (item) => renderViewedContactDetailsCell(item), miWidth: "260px", maWidth: "260px" },
    ],
    [renderNameCell, renderJobTitleCell, renderViewedContactDetailsCell]
  );

  const calReqCredits = (selectedContacts, actionType) => {
    let res = 0;
    const creditInfo = credits[actionType];
    const keyToCheck = creditInfo.key;

    Object.keys(selectedContacts).forEach((key) => {
      const contact = selectedContacts[key];
      if (Array.isArray(keyToCheck)) {
        for (let i = 0; i < keyToCheck.length; i++) {
          if (!keyToCheck[i] || !contact[keyToCheck[i]]) {
            if (keyToCheck[i] === "isEmailViewed") {
              res += 1;
            }
            if (keyToCheck[i] === "isPhoneViewed") {
              res += 5;
            }
          }
        }
      } else if (!keyToCheck || !contact[keyToCheck]) {
        res += creditInfo.credit;
      }
    });

    return res;
  };

  const getActionableContactsCount = (selectedContactsMap, type) => {
    const list = Object.values(selectedContactsMap);
    if (type === "phone") {
      return list.filter((ele) => !ele?.isPhoneViewed && (ele?.isPhoneAvailable || ele?.hasPhone)).length;
    }
    if (type === "email") {
      return list.filter((ele) => !ele?.isEmailViewed && (ele?.isEmailAvailable || ele?.hasEmail)).length;
    }
    if (type === "both") {
      return list.filter((ele) => !((ele?.isEmailViewed || ele?.isLinkedInViewed) && ele?.isPhoneViewed) && ele?.hasPhone && (ele?.hasEmail || ele?.hasLinkedIn)).length;
    }
    return list.length;
  };

  return (
    <div>
      {!onLoadingChange && internalLoader && <Loader />}
      <div className="tw-flex tw-items-center tw-w-full tw-py-3">
        {isDataPresent && currentTab === tabEnum.searchContact && <NewContactForm clearFilter={clearFilter} dropdownData={selectedValues} searchButtonCalled={(e) => callContactsResults(e)} dataSource={dataSource} pathName={pathName} currentUserPlan={currentUserPlan} eventName={eventName ?? ""} />}
        {headerExtra && <div className="tw-ml-auto tw-flex-shrink-0">{headerExtra}</div>}
      </div>
      {currentTab === tabEnum.searchContact && selectedContacts && Object.keys(selectedContacts).length > 0 && (
        <div className="tw-w-fit tw-flex tw-items-center tw-mb-4">
          <SelectionBar
            type="contact"
            selectedCompanyArray={Object.keys(selectedContacts)}
            clearSelection={() => {
              setSelectedContacts({});
              setClearSelectedRows(true);
            }}
            selectedCompanyValuesArray={Object.values(selectedContacts)}
            viewPhoneEmail={(type) => handleViewAction(false, type)}
          />
        </div>
      )}

      {currentTab === tabEnum.searchContact && (
        <>
          <div className="tw-w-full tw-mb-6 tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
            {isLoading ? (
              <div className="tw-p-2">
                <LoadingSkeleton lines={10} lineHeight="44px" width="100%" shape="rectangle" />
              </div>
            ) : contactsDataList.length > 0 ? (
              <>
                <ReactTableTrademo
                  columns={searchColumns}
                  data={contactsDataList}
                  hideSorting
                  hideBorder
                  paginationPerPage={pageSize}
                  paginationDefaultPage={currentPage}
                  paginationTotalRows={totalContactCount || contactsDataList.length}
                  selectedData={[]}
                  clearSelectedRows={clearSelectedRows}
                  onSelectedRowsChange={(rows) => {
                    const nextSelected = {};
                    (rows || []).forEach((row, index) => {
                      if (isPlaceholderRow(row)) return;
                      const key = row?.contactId ?? `__row_${index}`;
                      nextSelected[key] = row;
                    });
                    setSelectedContacts(nextSelected);
                  }}
                  onChangeRowsPerPage={handleTablePageSizeChange}
                  onTableObjectChange={handleTableObjectChange}
                  onChangePage={handleTablePageChange}
                  clearFilter={() => undefined}
                  noDataComponent="No contacts found"
                />
                {pendingAction && (
                  <Modal centered show={confirmationModal} onHide={() => setConfirmationModal(false)} animation={false} className="confirm-modal" backdrop="static">
                    <Modal.Header>
                      <div className="w-100 d-flex">
                        <div className="col-md-6 m-auto">
                          <span className="header-confirm">Confirmation</span>
                        </div>
                        <div className="col-md-6 text-right pr-0">
                          <button className="btn btn-primary trademo-new-btn mx-2 px-2" onClick={handleConfirm}>
                            Confirm
                          </button>
                          <button type="button" className="close trademo-modal-close" aria-label="Close" onClick={handleModalClose}>
                            <span aria-hidden="true">&times;</span>
                          </button>
                        </div>
                      </div>
                    </Modal.Header>
                    <Modal.Body>
                      <p className="modaltext mb-0">
                        You are about to view {pendingAction.type === "both" ? "contact information" : pendingAction.type} for <strong>{!pendingAction.item ? `${getActionableContactsCount(selectedContacts, pendingAction.type)} ${getActionableContactsCount(selectedContacts, pendingAction.type) > 1 ? "Contacts" : "Contact"}` : pendingAction.item.fullName}</strong>, which will cost you <strong>{!pendingAction.item ? calReqCredits(selectedContacts, pendingAction.type) : credits[pendingAction.type].credit} Value Add Credit(s)</strong>
                      </p>
                    </Modal.Body>
                  </Modal>
                )}
              </>
            ) : successModal ? (
              <div className="mt-5 py-5 mx-auto d-block text-center">
                <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1669378628/intel/salesforce/checkbox-circle-fill_leitkv.png" alt="thankyou_icon" />
                <span className="d-block">We have received your request. </span>
                <span className="d-block"> Once we find contacts as per your request, </span>
                <span className="d-block mb-3"> we will share them over email in next 24 business hours</span>
                <span
                  className="back_button_contact mt-3 cursor-pointer"
                  onClick={() => {
                    setContactSearched(true);
                    setSuccessModal(false);
                    setShowRequestModal(false);
                    trademoTrackingEvents(trackingEventDictionary.companyProfilePageSearchContactsTabRequestContactsGoBack, generateTrackingObject(dataSource, pathName, currentUserPlan));
                  }}>
                  <img alt="no data found" height={72} src="https://res.cloudinary.com/drzta9shk/image/upload/v1681458718/intel/icons/keyboard_backspace_usnf67.svg" />
                  <span className="go_back_text ml-2">Go Back</span>
                </span>
              </div>
            ) : contactSearched && isFilterApplied ? (
              <div className="mx-auto d-flex flex-column justify-content-between text-center tw-w-[758px] tw-h-[304px] tw-rounded-lg tw-bg-white tw-pt-[42px] tw-pb-4 tw-px-[38px] tw-mt-4 tw-gap-4">
                <img className="tw-h-[72px]" alt="no_data_icon" src="https://res.cloudinary.com/drzta9shk/image/upload/v1682070462/intel/icons/no_contact_found_illustration_huhlpe.svg" />
                <p className="no-data-text">We are unable to find contacts from our integrated services. Modify your search criteria or use below options</p>
                <div className="d-flex align-items-center justify-content-center">
                  <button
                    onClick={() => {
                      let emptyObj = { department: [], seniority: [], location: [], job_title: [] };
                      callContactsResults(emptyObj);
                      setSelectedValues({ ...emptyObj });
                      setClearFilter(true);
                    }}
                    className="modify-button-called find-shipment-button mr-3 clear-button-called mr-2">
                    Clear filters
                  </button>

                  <button
                    onClick={() => {
                      if (STARTERPLAN) {
                        setShowGetInTouch(true);
                      } else {
                        trademoTrackingEvents(trackingEventDictionary.companyProfilePageSearchContactsTabRequestToFetchContacts, generateTrackingObject(dataSource, pathName, currentUserPlan));
                        setShowRequestModal(true);
                      }
                    }}
                    className="request-button-called tw-w-fit tw-flex tw-items-center">
                    Request our team to fetch contacts
                    <span>{STARTERPLAN && <img src={UpgradeIconSmall} alt="..." className="img-fluid mr-1" />}</span>
                  </button>
                </div>
                {showGetInTouch ? <GetInTouchPopup showGetInTouchPopup={showGetInTouch} handleGetInTouchClose={() => setShowGetInTouch(false)} profileType={profileTypeEnum.company} notifyCountry={STARTERPLANCOUNTRY} blockAccessType={"feature"} /> : null}
                <RequestContactFormNew remainingValueAddCount={data.valueAddCredit} dropdownFields={dropdownFields} data={props} userCreditsCount={data.userCredits} companyName={data.companyName} selectedValues={selectedValues} dataSource={dataSource} pathName={pathName} currentUserPlan={currentUserPlan} closeModal={() => setShowRequestModal(false)} showRequestModal={showRequestModal} successModal={successModal} setSuccessModal={(val) => setSuccessModal(val)} />
              </div>
            ) : contactSearched ? (
              <div className="mx-auto d-flex flex-column text-center tw-w-[758px] tw-h-[304px] tw-rounded-lg tw-bg-white tw-pt-[42px] tw-pb-4 tw-px-[38px] tw-mt-4 tw-gap-4">
                <img className="tw-h-[72px]" alt="no_data_icon" src="https://res.cloudinary.com/drzta9shk/image/upload/v1682070462/intel/icons/no_contact_found_illustration_huhlpe.svg" />
                <p className="no-data-text">Sorry, We are unable to find contacts. Please try the following alternatives.</p>
                <div className="d-flex align-items-center justify-content-center">
                  {profileContactData?.isContactScanDataPresent ? (
                    <button
                      onClick={() => {
                        setCurrentScanTab?.();
                        trademoTrackingEvents(trackingEventDictionary.companyProfileScanShipmentButton, generateTrackingObject(dataSource, pathName, currentUserPlan));
                      }}
                      className="modify-button-called find-shipment-button mr-3 d-flex align-items-center">
                      <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1715753783/intel/icons/magic-wand_1_1_vfwgw6.svg" alt="search" />
                      Discover Extracted contacts
                    </button>
                  ) : (
                    <></>
                  )}
                  <button
                    onClick={() => {
                      if (STARTERPLAN) {
                        setShowGetInTouch(true);
                      } else {
                        trademoTrackingEvents(trackingEventDictionary.companyProfilePageSearchContactsTabRequestToFetchContacts, generateTrackingObject(dataSource, pathName, currentUserPlan));
                        setShowRequestModal(true);
                      }
                    }}
                    className="request-button-called tw-w-fit tw-flex tw-items-center">
                    Request our team to fetch contacts
                    <span>{STARTERPLAN && <img src={UpgradeIconSmall} alt="..." className="img-fluid mr-1" />}</span>
                  </button>
                </div>
                {showGetInTouch ? <GetInTouchPopup showGetInTouchPopup={showGetInTouch} handleGetInTouchClose={() => setShowGetInTouch(false)} profileType={profileTypeEnum.company} notifyCountry={STARTERPLANCOUNTRY} blockAccessType={"feature"} /> : null}
                <RequestContactFormNew remainingValueAddCount={data.valueAddCredit} dropdownFields={dropdownFields} data={props} userCreditsCount={data.userCredits} companyName={data.companyName} selectedValues={selectedValues} dataSource={dataSource} pathName={pathName} currentUserPlan={currentUserPlan} closeModal={() => setShowRequestModal(false)} showRequestModal={showRequestModal} successModal={successModal} setSuccessModal={(val) => setSuccessModal(val)} />
              </div>
            ) : (
              <></>
            )}
          </div>
          {!isLoading && contactsDataList.length > 0 && (
            <>
              <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-font-semibold tw-pb-4">
                <span className="tw-text-sm">Unable to find the relevant contacts?</span>
                <div
                  className="tw-text-white tw-bg-primary-purple-600 tw-cursor-pointer tw-flex tw-items-center tw-py-1.5 tw-gap-1 tw-px-3 tw-border tw-border-primary-purple-600 tw-rounded-lg"
                  onClick={() => {
                    if (STARTERPLAN) {
                      setShowGetInTouch(true);
                    } else {
                      trademoTrackingEvents(trackingEventDictionary.companyProfilePageSearchContactsTabRequestToFetchContacts, generateTrackingObject(dataSource, pathName, currentUserPlan));
                      setShowRequestModal(true);
                    }
                  }}>
                  <span className="material-symbols-outlined tw-w-4 tw-h-4 tw-flex tw-items-center">contacts</span>
                  <span className="tw-text-xs">Request our team to fetch contacts</span>
                  <span>{STARTERPLAN && <img src={UpgradeIconSmall} alt="..." className="img-fluid mr-1" />}</span>
                </div>
              </div>
              {showRequestModal && <RequestContactFormNew remainingValueAddCount={data.valueAddCredit} dropdownFields={dropdownFields} data={props} userCreditsCount={data.userCredits} companyName={data.companyName} selectedValues={selectedValues} dataSource={dataSource} pathName={pathName} currentUserPlan={currentUserPlan} closeModal={() => setShowRequestModal(false)} showRequestModal={showRequestModal} successModal={successModal} setSuccessModal={(val) => setSuccessModal(val)} />}
              {showGetInTouch ? <GetInTouchPopup showGetInTouchPopup={showGetInTouch} handleGetInTouchClose={() => setShowGetInTouch(false)} profileType={profileTypeEnum.company} notifyCountry={STARTERPLANCOUNTRY} blockAccessType={"feature"} /> : null}
            </>
          )}
        </>
      )}
      {currentTab === tabEnum.viewedContact && (
        <div>
          <div className="tw-w-full tw-mb-6 tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden tw-py-3">
            <ReactTableTrademo key={pageSize} columns={viewedColumns} data={viewContactList} hideSelection hideSorting hideBorder paginationPerPage={pageSize} paginationDefaultPage={currentPage} paginationTotalRows={viewContactList?.length ?? 0} selectedData={[]} onSelectedRowsChange={() => undefined} onChangeRowsPerPage={handleTablePageSizeChange} onTableObjectChange={handleTableObjectChange} onChangePage={() => undefined} clearFilter={() => undefined} noDataComponent="No viewed contacts found" />
            {pendingAction && (
              <Modal centered show={confirmationModal} onHide={() => setConfirmationModal(false)} animation={false} className="confirm-modal" backdrop="static">
                <Modal.Header>
                  <div className="w-100 d-flex">
                    <div className="col-md-6 m-auto">
                      <span className="header-confirm">Confirmation</span>
                    </div>
                    <div className="col-md-6 text-right pr-0">
                      <button className="btn btn-primary trademo-new-btn mx-2 px-2" onClick={handleConfirm}>
                        Confirm
                      </button>
                      <button type="button" className="close trademo-modal-close" aria-label="Close" onClick={handleModalClose}>
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  </div>
                </Modal.Header>
                <Modal.Body>
                  <p className="modaltext mb-0">
                    You are about to view {pendingAction.type === "both" ? "contact information" : pendingAction.type} for <strong>{pendingAction.item?.fullName}</strong>, which will cost you <strong>{credits[pendingAction.type].credit} Value Add Credit(s)</strong>
                  </p>
                </Modal.Body>
              </Modal>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchContacts;
